import { useState, useEffect, useRef } from 'react';

import { 
  getCustomerPropertyTypes, 
  getPropertyValuesByType, 
  getCustomerPropertiesByCustomerCode, 
  saveCustomerProperties,
  deleteCustomerProperty
} from '../services/customerPropertiesApi';

export const useCustomerProperties = (customerCode?: string) => {
  const DEBUG = process.env.NODE_ENV !== 'production';
  // Özellik tipleri ve değerleri için state'ler
  const [propertyTypes, setPropertyTypes] = useState<any[]>([]);
  const [propertyValues, setPropertyValues] = useState<Record<number, any[]>>({});
  
  // Müşteri özellikleri için state
  const [customerProperties, setCustomerProperties] = useState<Record<number, string>>({});
  
  // Yükleme ve hata durumları için state'ler
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  // Tüm özellik tiplerini yükle
  const loadPropertyTypes = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getCustomerPropertyTypes();
      if (DEBUG) console.log('Yüklenen özellik tipleri:', data);
      setPropertyTypes(data);
      return data;
    } catch (err) {
      setError(err as Error);
      if (DEBUG) console.error('Müşteri özellik tipleri yüklenirken hata:', err);
      return [];
    } finally {
      setLoading(false);
    }
  };

  // Belirli bir özellik tipi için değerleri yükle
  const loadPropertyValues = async (attributeTypeCode: number) => {
    try {
      setLoading(true);
      setError(null);
      const data = await getPropertyValuesByType(attributeTypeCode);
      if (DEBUG) console.log(`${attributeTypeCode} tipindeki özellik değerleri:`, data);
      setPropertyValues(prev => ({ ...prev, [attributeTypeCode]: data }));
      return data;
    } catch (err) {
      setError(err as Error);
      if (DEBUG) console.error(`${attributeTypeCode} tipindeki özellik değerleri yüklenirken hata:`, err);
      return [];
    } finally {
      setLoading(false);
    }
  };

  // Tüm özellik tiplerini ve değerlerini yükle
  const loadProperties = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Önce özellik tiplerini yükle
      const types = await loadPropertyTypes();
      
      // Sonra her tip için değerleri yükle
      const valuesPromises = types.map((type: { typeCode: number }) => loadPropertyValues(type.typeCode));
      await Promise.all(valuesPromises);
      
      return true;
    } catch (err) {
      setError(err as Error);
      if (DEBUG) console.error('Müşteri özellikleri yüklenirken hata:', err);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Belirli bir müşterinin özelliklerini yükle
  const loadCustomerProperties = async (code: string) => {
    if (!code) return;
    
    try {
      setLoading(true);
      setError(null);
      const data = await getCustomerPropertiesByCustomerCode(code);
      if (DEBUG) console.log(`${code} kodlu müşterinin özellikleri:`, data);
      setCustomerProperties(data);
      return data;
    } catch (err) {
      setError(err as Error);
      if (DEBUG) console.error(`${code} kodlu müşterinin özellikleri yüklenirken hata:`, err);
      return {} as Record<number, string>;
    } finally {
      setLoading(false);
    }
  };

  // Müşteri özelliklerini kaydet
  const saveProperties = async (properties: Record<number, string>) => {
    if (!customerCode) return;
    
    try {
      setLoading(true);
      setError(null);
      const result = await saveCustomerProperties(customerCode, properties);
      setCustomerProperties(properties); // Başarılı olursa local state'i güncelle
      return result;
    } catch (err) {
      setError(err as Error);
      if (DEBUG) console.error('Müşteri özellikleri kaydedilirken hata:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Müşteri özelliğini sil
  const deleteProperty = async (attributeTypeCode: number) => {
    if (!customerCode) return;
    
    try {
      setLoading(true);
      setError(null);
      const result = await deleteCustomerProperty(customerCode, attributeTypeCode);
      
      // Başarılı olursa local state'den de sil
      const isSuccess = (result && (result.success === true || result.Success === true))
        || (result?.data && (result.data.success === true || result.data.Success === true));
      if (isSuccess) {
        setCustomerProperties(prev => {
          const newProps = { ...prev };
          delete newProps[attributeTypeCode];
          return newProps;
        });
      }
      
      return result;
    } catch (err) {
      setError(err as Error);
      if (DEBUG) console.error('Müşteri özelliği silinirken hata:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Müşteri kodu değiştiğinde müşteri özelliklerini yükle
  useEffect(() => {
    if (customerCode && customerCode !== 'temp') {
      loadCustomerProperties(customerCode);
    } else {
      setCustomerProperties({});
    }
  }, [customerCode]);

  // Bileşen yüklendiğinde tüm özellik tiplerini ve değerlerini yükle
  // StrictMode'da mount-unmount ikilemesinde iki defa çalışmayı azaltmak için guard
  const mountedOnceRef = useRef<boolean>(false);
  useEffect(() => {
    if (mountedOnceRef.current) return;
    mountedOnceRef.current = true;
    loadProperties();
  }, []);

  return {
    propertyTypes,
    propertyValues,
    customerProperties,
    loading,
    error,
    loadPropertyTypes,
    loadPropertyValues,
    loadProperties,
    loadCustomerProperties,
    saveProperties,
    deleteProperty
  };
};
